import { runInAction, observable, action, makeObservable } from 'mobx'
import { types as t } from '@invisr/mobx-state-tree'
import crudActions from './crud-actions'
import fetch from '../../utils/fetch'
import errorToast from '../../utils/error-toast'
import toRecords from './record/record'

export const callCognito = (actionName, method: 'GET'|'PUT'|'POST', body?: Record<string, any>) => {
  return fetch(`/api/${actionName}`, {
    method: method,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    },
    mode: 'cors',
    body: JSON.stringify(body)
  }).then(res => res.json())
}

interface UsersProviderConfig {
  id: string
}
class UsersProvider {
  data = []
  isLoading = false
  dataFetched = false
  config: UsersProviderConfig
  constructor (config?: UsersProviderConfig) {
    makeObservable(this, {
      data: observable,
      isLoading: observable,
      setIsLoading: action,
      setIsLoadingOff: action,
      query: action
    })

    this.config = config
  }

  setIsLoading = () => {
    this.isLoading = true
  }

  setIsLoadingOff = () => {
    this.isLoading = false
  }

  lookup = async (searchValue = '', refData) => {
    if (!this.data) await this.query()
    if (!refData) return []
    const { label, value } = refData

    const matches = this.data
      .filter(item => {
        let found
        if (typeof item[label] === 'string') {
          found =
            item[label].toLowerCase().indexOf(searchValue.toLowerCase()) !== -1
        }
        if (!found && typeof item[value] === 'string') {
          found = item[value] === searchValue
        }
        return found
      })
    return matches
  }

  query = () => {
    this.setIsLoading()
    if (this.dataFetched) return this.data
    this.dataFetched = true
    callCognito('users/listUsers', 'GET')
      .then(
        action(result => {
          result.Users.forEach(user => {
            if (user.Attributes) {
              user.Attributes.forEach(attr => {
                user[attr.Name] = attr.Value
              })
            }
            delete user.Attributes
          })
          runInAction(() => {
            this.data = toRecords({ recordType: this.config.id }, result.Users)
          })
        })
      )
      .catch(er => {
        console.log(er)
        errorToast('Error fetching users: ', er)
      })
      .then(this.setIsLoadingOff)
  }
}

export default t
  .model('UsersConfig', {
    type: t.optional(t.string, 'Users')
  })
  .views(self => ({
    get fieldSet () {
      return ['_id', 'Username'].map(fieldPath => ({
        fieldPath,
        type: 'STRING',
        label: fieldPath
      }))
    },
    labels () {
      const fields = this.fieldSet.map(attr => {
        return {
          key: attr.fieldPath + '',
          label: attr.label + '',
          secondaryLabel: attr.type + '',
          type: attr.type + '',
          meta: {
            fieldPath: attr.fieldPath,
            label: attr.label,
            type: attr.type
          }
        }
      })

      const childNodes = Object.keys(new UsersProvider())
        .filter(k => typeof new UsersProvider()[k] === 'function')
        .map(f => ({
          key: f,
          label: f,
          secondaryLabel: 'FUNCTION'
        }))

      return {
        key: (self as any).id as string,
        label: (self as any).name as string,
        childNodes: [
          {
            key: 'methods',
            label: 'Methods',
            isExpanded: false,
            childNodes
          },
          {
            key: 'data',
            label: 'Data',
            asynch: true,
            childNodes: fields
          }
        ]
      }
    }
  }))
  .views(self => ({
    get icon () {
      return 'people'
    },
    get provider () {
      return UsersProvider
    },
    fieldPath (node) {
      return node.meta && node.meta.fieldPath
    },
    getFields () {
      return self.fieldSet
    },
    fetchChildNodes () {
      return self && self.labels().childNodes[1].childNodes
    },
  }))
  .actions(crudActions)
